import { Link } from "react-router-dom";
import { useState } from "react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Mountain, Compass, KeyRound, Sparkles, Printer, Copy, ExternalLink, ArrowRight } from "lucide-react";

const StudentLoginGuide = () => {
  const [copied, setCopied] = useState(false);
  // Teachers/students will land on THIS site's /student/login. Use the current origin
  // so the guide always points at the right place (preview, prod, or a custom domain).
  const loginUrl = `${window.location.origin}/student/login`;

  const copyLink = () => {
    navigator.clipboard?.writeText(loginUrl).then(
      () => { setCopied(true); toast.success("Link copied!"); setTimeout(() => setCopied(false), 1600); },
      () => toast.error("Could not copy")
    );
  };

  const doPrint = () => window.print();

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo print:bg-white">
      {/* Print-friendly styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .step-card { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Top bar (hidden in print) */}
      <div className="no-print border-b-2 border-mm-navy/10 bg-white/70 backdrop-blur">
        <div className="container mx-auto max-w-4xl py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-heading font-black text-mm-navy hidden sm:inline">Mind Map</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="btn-trail btn-outline-navy text-xs" data-testid="copy-login-link-btn">
              <Copy size={12} strokeWidth={2.5} /> {copied ? "Copied!" : "Copy login link"}
            </button>
            <button onClick={doPrint} className="btn-trail btn-outline-navy text-xs" data-testid="print-guide-btn">
              <Printer size={12} strokeWidth={2.5} /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl py-10 px-4">
        {/* Hero */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel" data-testid="student-guide-eyebrow">Trailhead — Student Login</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-mm-navy mt-3" data-testid="student-guide-title">
            Ready, Explorer?
          </h1>
          <p className="text-mm-navy/75 mt-4 text-lg max-w-2xl mx-auto">
            Follow these three steps to start your adventure. Your teacher will give you your explorer <b>username</b> and a secret <b>4-digit PIN</b>.
          </p>
        </div>

        {/* Login URL callout */}
        <div className="mt-8 rounded-2xl border-2 border-mm-gold/50 bg-mm-gold/10 p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="student-guide-url-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-mm-gold flex items-center justify-center shrink-0">
              <Compass size={22} strokeWidth={2.5} className="text-mm-navy" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Go to this website</div>
              <div className="font-heading font-black text-mm-navy text-xl sm:text-2xl break-all" data-testid="student-guide-url">{loginUrl}</div>
            </div>
          </div>
          <a
            href={loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-trail btn-gold text-sm shrink-0 no-print"
            data-testid="open-student-login-btn"
          >
            Open <ExternalLink size={14} strokeWidth={2.5} />
          </a>
        </div>

        {/* Steps */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Step 1 */}
          <div className="step-card rounded-2xl bg-white border-2 border-mm-navy/10 p-6" data-testid="guide-step-1">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-mm-navy text-white flex items-center justify-center font-heading font-black text-xl">1</div>
              <Mountain size={22} strokeWidth={2} className="text-mm-steel" />
            </div>
            <h3 className="font-heading text-xl font-black text-mm-navy mt-4">Open the trail</h3>
            <p className="text-sm text-mm-navy/75 mt-2">
              Type <span className="font-mono font-bold text-mm-navy break-all">{loginUrl}</span> into your web browser (like Chrome or Safari).
            </p>
            {/* Mock browser preview */}
            <div className="mt-4 rounded-lg border-2 border-mm-navy/10 overflow-hidden">
              <div className="bg-mm-parchment px-3 py-1.5 flex items-center gap-1 border-b border-mm-navy/10">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="w-2 h-2 rounded-full bg-yellow-400" />
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="ml-2 text-[10px] font-mono text-mm-navy/70 truncate">{loginUrl}</span>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Trailhead</div>
                <div className="font-heading text-sm font-black text-mm-navy mt-0.5">Enter your trail</div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="step-card rounded-2xl bg-white border-2 border-mm-navy/10 p-6" data-testid="guide-step-2">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-mm-navy text-white flex items-center justify-center font-heading font-black text-xl">2</div>
              <Compass size={22} strokeWidth={2} className="text-mm-steel" />
            </div>
            <h3 className="font-heading text-xl font-black text-mm-navy mt-4">Type your username</h3>
            <p className="text-sm text-mm-navy/75 mt-2">
              Look at the card your teacher gave you. Type the <b>username</b> exactly as you see it — like <span className="font-mono font-bold text-mm-navy">Explorer1234</span>.
            </p>
            {/* Mock input */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Username</div>
              <div className="mt-1 border-2 border-mm-steel rounded-lg px-3 py-2.5 bg-white font-heading font-bold text-mm-navy text-lg">
                Explorer1234<span className="animate-pulse text-mm-steel">|</span>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="step-card rounded-2xl bg-white border-2 border-mm-navy/10 p-6" data-testid="guide-step-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-mm-navy text-white flex items-center justify-center font-heading font-black text-xl">3</div>
              <KeyRound size={22} strokeWidth={2} className="text-mm-steel" />
            </div>
            <h3 className="font-heading text-xl font-black text-mm-navy mt-4">Enter your 4-digit PIN</h3>
            <p className="text-sm text-mm-navy/75 mt-2">
              Type the <b>4 numbers</b> from your card into the PIN box. Keep your PIN secret — it's just for you!
            </p>
            {/* Mock PIN input */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">4-digit PIN</div>
              <div className="mt-1 flex gap-1.5">
                {[1, 7, 8, 7].map((n, i) => (
                  <div key={i} className="w-10 h-12 border-2 border-mm-steel rounded-lg flex items-center justify-center font-heading font-black text-mm-navy text-xl bg-white">
                    {n}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Then hit Enter Trail */}
        <div className="mt-8 rounded-2xl bg-mm-navy text-white p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap" data-testid="guide-final-step">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-mm-gold text-mm-navy flex items-center justify-center">
              <Sparkles size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-gold">Almost there</div>
              <div className="font-heading text-2xl font-black mt-1">Tap “Enter Trail” — you're in!</div>
              <p className="text-white/75 text-sm mt-1">The mountain is waiting. Start your first expedition and start earning altitude points.</p>
            </div>
          </div>
          <ArrowRight size={40} strokeWidth={2.5} className="text-mm-gold shrink-0 hidden sm:block" />
        </div>

        {/* Trouble helpers */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="guide-troubleshooting">
          <div className="rounded-xl bg-white border-2 border-mm-navy/10 p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Uh oh — it didn't work?</div>
            <ul className="mt-3 text-sm text-mm-navy/80 space-y-2 list-disc pl-5">
              <li>Check that your username is spelled exactly right (capital letters count!).</li>
              <li>Make sure your PIN has all 4 numbers.</li>
              <li>If it still won't let you in, ask your teacher for a new PIN.</li>
            </ul>
          </div>
          <div className="rounded-xl bg-white border-2 border-mm-navy/10 p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Explorer's Code</div>
            <ul className="mt-3 text-sm text-mm-navy/80 space-y-2 list-disc pl-5">
              <li>Keep your PIN a secret — don't share it with friends.</li>
              <li>Your username is a nickname. There's no last name or email needed.</li>
              <li>If you lose your card, your teacher can print a new one for you.</li>
            </ul>
          </div>
        </div>

        {/* Footer branding */}
        <div className="mt-12 text-center text-xs text-mm-navy/60">
          Mind Map · by The Meadows Mindset · Safe, anonymous student accounts
        </div>
      </div>
    </div>
  );
};

export default StudentLoginGuide;
